/** LCS is O(n·m) — skip diffing huge payloads and fall back to side-by-side. */
export const MAX_DIFF_LINES = 1500;

type DiffLineType = 'same' | 'added' | 'removed';
interface DiffLine {
  type: DiffLineType;
  text: string;
}

export const LINE_CLASS: Record<DiffLineType, string> = {
  added: 'bg-emerald-500/10 text-emerald-900 dark:text-emerald-200',
  removed: 'bg-rose-500/10 text-rose-900 dark:text-rose-200',
  same: 'text-foreground/80',
};
export const LINE_SIGN: Record<DiffLineType, string> = { added: '+', removed: '-', same: ' ' };

/**
 * Line-based unified diff over two texts (LCS).
 *
 * Compiles under `noUncheckedIndexedAccess`: every dynamic index read goes
 * through `aLine`/`bLine`/`cell`. The `?? ''` / `?? 0` fallbacks are no-ops
 * for in-bounds indices — String.split never yields undefined and
 * Uint32Array cells are zero-initialized — they only define behavior if the
 * loop invariants were ever violated.
 */
export function buildLineDiff(oldText: string, newText: string): DiffLine[] {
  const a = oldText.split('\n');
  const b = newText.split('\n');
  const n = a.length;
  const m = b.length;

  const aLine = (i: number): string => a[i] ?? '';
  const bLine = (j: number): string => b[j] ?? '';
  const cell = (row: Uint32Array | undefined, j: number): number => row?.[j] ?? 0;

  // dp[i][j] = LCS length of a[i..] vs b[j..]. rows[n] is the all-zero base
  // row; each pass computes one row from the row below it.
  let below: Uint32Array = new Uint32Array(m + 1); // dp[n]
  const rows: Uint32Array[] = [below];
  for (let i = n - 1; i >= 0; i--) {
    const cur = new Uint32Array(m + 1); // dp[i]
    for (let j = m - 1; j >= 0; j--) {
      cur[j] =
        aLine(i) === bLine(j) ? cell(below, j + 1) + 1 : Math.max(cell(below, j), cell(cur, j + 1));
    }
    rows.push(cur);
    below = cur;
  }
  rows.reverse();

  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (aLine(i) === bLine(j)) {
      out.push({ type: 'same', text: aLine(i) });
      i += 1;
      j += 1;
    } else if (cell(rows[i + 1], j) >= cell(rows[i], j + 1)) {
      out.push({ type: 'removed', text: aLine(i) });
      i += 1;
    } else {
      out.push({ type: 'added', text: bLine(j) });
      j += 1;
    }
  }
  while (i < n) {
    out.push({ type: 'removed', text: aLine(i) });
    i += 1;
  }
  while (j < m) {
    out.push({ type: 'added', text: bLine(j) });
    j += 1;
  }
  return out;
}

/** Pretty-print JSON (string or already-parsed object); falls back to raw text. */
export function prettyJson(raw: unknown): string {
  if (raw == null || raw === '') return '';
  if (typeof raw === 'string') {
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  }
  try {
    return JSON.stringify(raw, null, 2);
  } catch {
    return String(raw);
  }
}